'use client'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { trackFuelAlertSignup } from '@/lib/tracking'

/**
 * The only interactive part of the fuel-alerts section. Kept as its own client
 * island so the surrounding page can stay a server component and ship no JS
 * for the ~95% of it that is static copy.
 */
export function FuelAlertForm() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email) return

    setIsSubmitting(true)
    setSubmitStatus('idle')

    try {
      const response = await fetch('/api/fuel-alerts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })

      if (response.ok) {
        trackFuelAlertSignup(email)
        setSubmitStatus('success')
        setEmail('')
      } else {
        setSubmitStatus('error')
      }
    } catch {
      setSubmitStatus('error')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitStatus === 'success') {
    return (
      <div className="mt-8 p-4 bg-accent/20 rounded-md max-w-md mx-auto">
        <p className="text-primary-foreground font-medium">
          You&apos;re subscribed! Watch for weekly fuel price updates in your inbox.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
      <label htmlFor="fuel-alert-email" className="sr-only">
        Email address
      </label>
      <input
        id="fuel-alert-email"
        name="email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Enter your email"
        required
        disabled={isSubmitting}
        className="flex-1 px-4 py-3 rounded-md bg-primary-foreground/10 border border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50 focus:outline-none focus:ring-2 focus:ring-accent text-sm disabled:opacity-50"
      />
      <Button
        type="submit"
        disabled={isSubmitting}
        className="bg-accent text-accent-foreground hover:bg-accent/90 font-medium text-sm tracking-wide px-6 py-3 h-auto disabled:opacity-50"
      >
        {isSubmitting ? 'Subscribing...' : 'Subscribe'}
      </Button>
      {submitStatus === 'error' && (
        <p className="text-red-300 text-sm mt-2 sm:col-span-2">Something went wrong. Please try again.</p>
      )}
    </form>
  )
}
